import type { CatalogItem, ConnectedSupplierSummary, OrderListItem } from "@dishour/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { unwrap } from "./client.js";

const get = <T,>(path: string) => fetch(path, { credentials: "same-origin" }).then(unwrap<T>);
const post = <T,>(path: string, body?: unknown) =>
  fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: body === undefined ? {} : { "content-type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  }).then(unwrap<T>);

export const mySuppliersKey = ["orders", "suppliers"] as const;
export const myOrdersKey = ["orders", "mine"] as const;
export const supplierCatalogKey = (supplierId: string) => ["orders", "catalog", supplierId] as const;

export const useMySuppliers = () =>
  useQuery({ queryKey: mySuppliersKey, queryFn: () => get<ConnectedSupplierSummary[]>("/api/suppliers") });

export const useSupplierCatalog = (supplierId: string | null) =>
  useQuery({
    queryKey: supplierCatalogKey(supplierId ?? ""),
    queryFn: () => get<CatalogItem[]>(`/api/suppliers/${supplierId}/catalog`),
    enabled: supplierId !== null,
  });

export const useMyOrders = () =>
  useQuery({ queryKey: myOrdersKey, queryFn: () => get<OrderListItem[]>("/api/orders") });

export function useCreateOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { supplierId: string; lines: { skuId: string; qty: string }[] }) =>
      post<OrderListItem>("/api/orders", body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: myOrdersKey }),
  });
}

export function useCancelOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => post<OrderListItem>(`/api/orders/${orderId}/cancel`),
    onSuccess: (updated) =>
      queryClient.setQueryData<OrderListItem[]>(myOrdersKey, (prev) =>
        prev?.map((o) => (o.id === updated.id ? updated : o)),
      ),
  });
}
